import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { MessagingRouteService } from './messaging-route.service';
import { MessagingStoreService } from './messaging-store.service';
import { MessagingNotification, MessagingThreadSummary } from './messaging.models';

@Component({
    selector: 'app-messaging-dropdown-bell',
    standalone: true,
    imports: [AsyncPipe, DatePipe, MatBadgeModule, MatButtonModule, MatDividerModule, MatIconModule, MatMenuModule, ReferenceDisplayDirective],
    templateUrl: './messaging-dropdown-bell.component.html',
    styleUrl: './messaging-dropdown-bell.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagingDropdownBellComponent implements OnInit {
    private readonly store = inject(MessagingStoreService);
    private readonly routeService = inject(MessagingRouteService);
    private readonly router = inject(Router);

    // Show unread first, then recent read; limit to 5 for the dropdown preview
    readonly previewNotifications$: Observable<MessagingNotification[]> = this.store.notifications$.pipe(
        map((notifications) => {
            const sorted = [...(notifications ?? [])].sort((a, b) => {
                const aUnread = (a.status ?? '') !== 'read';
                const bUnread = (b.status ?? '') !== 'read';
                if (aUnread !== bUnread) return aUnread ? -1 : 1;
                const aTs = new Date(a.createdAt ?? 0).getTime();
                const bTs = new Date(b.createdAt ?? 0).getTime();
                return bTs - aTs;
            });
            return sorted.slice(0, 5);
        })
    );

    readonly totalUnreadMessages$: Observable<number> = this.store.threads$.pipe(
        map((threads) => threads.reduce((sum, thread) => sum + Math.max(0, Number(thread.unreadCount ?? 0)), 0))
    );

    readonly latestNotification$: Observable<MessagingNotification | null> = this.store.notifications$.pipe(
        map((notifications) => notifications[0] ?? null)
    );

    ngOnInit(): void {
        this.store.bootstrap();
    }

    openThread(thread: MessagingThreadSummary): void {
        if ((thread.unreadCount ?? 0) > 0) {
            this.store.markThreadRead(thread.threadId).subscribe({
                error: (error) => console.error('Failed to mark dropdown-opened thread as read', error)
            });
        }

        const route = this.routeService.buildThreadRoute(thread);
        this.store.setActiveThread(thread.threadId);
        void this.router.navigate([route.routePath], { queryParams: route.queryParams });
    }

    openNotification(notification: MessagingNotification): void {
            this.store.markNotificationOpened(notification.notificationId).subscribe({
                error: (error) => console.error('Failed to mark notification opened', error)
            });

            const route = this.routeService.resolveNotificationRoute(notification);
            if (!route?.routePath) return;

            this.router.navigate([route.routePath], { queryParams: route.queryParams })
                .then((succeeded) => {
                    if (succeeded) {
                        this.store.markNotificationRead(notification.notificationId).subscribe({
                            error: (error) => console.error('Failed to mark notification read', error)
                        });
                    }
                })
                .catch((err) => console.error('Navigation failed', err));
    }

    openLatestThread(latestThread: MessagingThreadSummary | null): void {
        if (!latestThread) {
            void this.router.navigate(['/app/messaging']);
            return;
        }

        this.openThread(latestThread);
    }

    openInbox(): void {
        void this.router.navigate(['/app/messaging']);
    }

    openLatestNotificationFromValue(latestNotification: MessagingNotification | null | undefined): void {
        if (!latestNotification) {
            void this.router.navigate(['/app/messaging']);
            return;
        }

        this.openNotification(latestNotification);
    }

    getSenderLabel(thread: MessagingThreadSummary): string {
        return (thread.lastSender?.display ?? thread.lastSender?.participantId ?? 'Unknown sender').toString();
    }

    getContextLabel(thread: MessagingThreadSummary): string | null {
        const context = thread.context ?? {};

        if (context.serviceRequestId) return `ServiceRequest ${context.serviceRequestId}`;
        if (context.medicationRequestId) return `MedicationRequest ${context.medicationRequestId}`;
        if (context.medicationDispenseId) return `MedicationDispense ${context.medicationDispenseId}`;
        if (context.diagnosticReportId) return `DiagnosticReport ${context.diagnosticReportId}`;
        if (context.contextType || context.contextId) {
            return [context.contextType, context.contextId].filter(Boolean).join(' ');
        }

        return null;
    }

    getContextReference(thread: MessagingThreadSummary): string | null {
        const context = thread.context ?? {};

        if (context.serviceRequestId) return `ServiceRequest/${context.serviceRequestId}`;
        if (context.medicationRequestId) return `MedicationRequest/${context.medicationRequestId}`;
        if (context.medicationDispenseId) return `MedicationDispense/${context.medicationDispenseId}`;
        if (context.diagnosticReportId) return `DiagnosticReport/${context.diagnosticReportId}`;

        const subject = thread.subject;
        if (subject?.resourceType && subject?.resourceId) {
            return `${subject.resourceType}/${subject.resourceId}`;
        }

        return null;
    }
}
